/**
 * MIC-1–3 — full-screen materials inventory count shell.
 */
import { useEffect } from "react";
import type {
  MfgInventoryCountLine,
  MfgInventoryCountProposal,
  MfgInventoryCountSession,
} from "@/lib/manufacturing/types/inventoryCount";
import {
  adminDangerGhostBtnClass,
  ghostBtnClass,
  mfgModalShellClass,
  mfgPrimaryBtnClass,
  mfgPrimaryBtnLgClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingInventoryCountDictate } from "./ManufacturingInventoryCountDictate";
import { ManufacturingInventoryCountLinesPanel } from "./ManufacturingInventoryCountLinesPanel";
import { ManufacturingInventoryCountProposalsPanel } from "./ManufacturingInventoryCountProposalsPanel";

type Props = {
  open: boolean;
  enabled: boolean;
  busy: boolean;
  error: string | null;
  session: MfgInventoryCountSession | null;
  lines: MfgInventoryCountLine[];
  proposals: MfgInventoryCountProposal[];
  savingLineId: string | null;
  proposing: boolean;
  reviewingId: string | null;
  canReviewProposals: boolean;
  onOpenSession: () => void;
  onCloseSession: () => void;
  onDiscardSession: () => void;
  onExit: () => void;
  onMountOpen: () => void;
  onCommitQty: (lineId: string, countedQty: number | null) => void;
  onProposeNewItem: (opts: {
    proposed_name: string;
    proposed_unit: string;
    proposed_category: string;
    proposed_qty: number | null;
  }) => void;
  onApproveProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onRefreshProposals: () => void;
  onDownloadDraft?: () => void;
};

export function ManufacturingInventoryCountShell({
  open,
  enabled,
  busy,
  error,
  session,
  lines,
  proposals,
  savingLineId,
  proposing,
  reviewingId,
  canReviewProposals,
  onOpenSession,
  onCloseSession,
  onDiscardSession,
  onExit,
  onMountOpen,
  onCommitQty,
  onProposeNewItem,
  onApproveProposal,
  onRejectProposal,
  onRefreshProposals,
  onDownloadDraft,
}: Props) {
  useEffect(() => {
    if (!open) return;
    onMountOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onExit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onExit]);

  if (!open) return null;

  const isOpen = session?.status === "open";
  const counted = lines.filter((l) => l.counted_qty != null).length;
  const total = lines.length;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Inventory count"
    >
      <div className={`flex h-full w-full flex-col ${mfgModalShellClass} rounded-none`}>
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-zinc-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-50">
              Inventory count
            </h2>
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              {session
                ? `Session ${session.status} · ${counted}/${total} counted`
                : busy
                  ? "Opening session…"
                  : "No session"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isOpen && onDownloadDraft ? (
              <button
                type="button"
                className={ghostBtnClass}
                disabled={busy || lines.length === 0}
                onClick={onDownloadDraft}
                title="Download local JSON draft (BULKINV-YYYYMMDD.json)"
              >
                Download draft
              </button>
            ) : null}
            {isOpen ? (
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={busy}
                onClick={onCloseSession}
              >
                Submit count
              </button>
            ) : null}
            {isOpen ? (
              <button
                type="button"
                className={`${adminDangerGhostBtnClass} px-3 py-1.5`}
                disabled={busy}
                onClick={onDiscardSession}
              >
                Discard count
              </button>
            ) : null}
            <button type="button" className={ghostBtnClass} onClick={onExit} disabled={busy}>
              Exit
            </button>
          </div>
        </header>

        {error ? (
          <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {!session && !busy ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-gray-600 dark:text-zinc-300">
                Start a plant count session to snapshot expected on-hand quantities.
              </p>
              <button
                type="button"
                className={mfgPrimaryBtnLgClass}
                disabled={!enabled || busy}
                onClick={onOpenSession}
              >
                Open count session
              </button>
            </div>
          ) : (
            <>
              <ManufacturingInventoryCountDictate
                lines={lines}
                sessionOpen={!!isOpen}
                busy={busy}
                onCommitQty={onCommitQty}
              />
              <ManufacturingInventoryCountLinesPanel
                lines={lines}
                busy={busy}
                sessionOpen={!!isOpen}
                savingLineId={savingLineId}
                proposing={proposing}
                onCommitQty={onCommitQty}
                onProposeNewItem={onProposeNewItem}
              />
              {session ? (
                <ManufacturingInventoryCountProposalsPanel
                  proposals={proposals}
                  canReview={canReviewProposals}
                  busy={busy}
                  reviewingId={reviewingId}
                  onApprove={onApproveProposal}
                  onReject={onRejectProposal}
                  onRefresh={onRefreshProposals}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
